/** Shared across chimes — browsers cap the number of live AudioContexts per page. */
let context: AudioContext | null = null;
let waitingForGesture = false;

/**
 * Unlocks audio from the first click or keypress on the page.
 *
 * A chime is always triggered by a hub push, never by the user, so a context created at that
 * moment is born suspended: Safari only lets one start inside a gesture, and it stays suspended
 * for the rest of the page load, so every later resume() is rejected and the bell is silent for
 * the whole session. Creating it under a real gesture instead leaves it running and ready.
 */
export function primeNotificationChime(): void {
	if (context || waitingForGesture) {
		return;
	}
	waitingForGesture = true;

	const unlock = () => {
		document.removeEventListener('pointerdown', unlock);
		document.removeEventListener('keydown', unlock);
		waitingForGesture = false;

		try {
			context ??= new AudioContext();

			if (context.state !== 'running') {
				void context.resume().catch(() => undefined);
			}
		} catch {
			/** no audio output / API unavailable — stay silent */
		}
	};

	document.addEventListener('pointerdown', unlock);
	document.addEventListener('keydown', unlock);
}

/**
 * Plays a short two-note "ding" synthesized with the Web Audio API, so no audio asset has to
 * ship with the bundle. If the page has never been interacted with, the context is still
 * suspended and resume() is rejected — the chime is skipped silently and the visual ring
 * still happens.
 */
export function playNotificationChime(): void {
	try {
		context ??= new AudioContext();
		const ctx = context;

		if (ctx.state === 'running') {
			chime(ctx);
			return;
		}

		ctx.resume()
			.then(() => chime(ctx))
			.catch(() => undefined);
	} catch {
		/** no audio output / API unavailable — stay silent */
	}
}

function chime(ctx: AudioContext): void {
	const now = ctx.currentTime;
	note(ctx, 987.77, now, 0.45); // B5
	note(ctx, 1318.51, now + 0.09, 0.7); // E6
}

function note(ctx: AudioContext, frequency: number, startAt: number, duration: number): void {
	const oscillator = ctx.createOscillator();
	const gain = ctx.createGain();

	oscillator.type = 'sine';
	oscillator.frequency.value = frequency;

	/** quick attack, exponential decay — reads as a soft bell rather than a beep */
	gain.gain.setValueAtTime(0, startAt);
	gain.gain.linearRampToValueAtTime(0.08, startAt + 0.01);
	gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

	oscillator.connect(gain).connect(ctx.destination);
	oscillator.start(startAt);
	oscillator.stop(startAt + duration);
}
